import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsSection = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ customer_name: '', text: '', rating: 5 });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.text.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      setSubmitted(true);
      setForm({ customer_name: '', text: '', rating: 5 });
      fetchReviews();
      setTimeout(() => { setSubmitted(false); setShowForm(false); }, 2000);
    } catch (err) {
      console.error('Failed to submit review:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20'}`}
        />
      ))}
    </div>
  );

  const renderStarsInput = () => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setForm({ ...form, rating: star })}
          className="focus:outline-none"
        >
          <Star
            className={`w-6 h-6 transition-colors ${star <= form.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20 hover:text-[#D4AF37]/50'}`}
          />
        </button>
      ))}
    </div>
  );

  return (
    <section id="reviews" className="py-16 bg-[#0A0A0A]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-4">
            {t('reviews_label') || 'Testimonials'}
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-white mb-3"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('reviews_title') || 'What Our Clients Say'}
          </h2>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            {t('reviews_subtitle') || 'Real experiences from our valued customers'}
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {reviews.slice(0, 6).map((review) => (
            <div
              key={review.id}
              className="bg-[#0d0d0d] border border-white/10 p-6 hover:border-[#D4AF37]/30 transition-colors"
              data-testid={`review-${review.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-serif text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {review.customer_name.charAt(0)}
                  </div>
                  <span className="text-white font-medium text-sm">{review.customer_name}</span>
                </div>
                {renderStars(review.rating)}
              </div>
              <p className="text-[#A1A1AA] text-sm leading-relaxed italic">
                "{review.text}"
              </p>
            </div>
          ))}
        </div>

        {/* Leave a Review */}
        <div className="text-center">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="px-8 py-3 border border-[#D4AF37] text-[#D4AF37] font-medium text-sm uppercase tracking-wider hover:bg-[#D4AF37] hover:text-black transition-colors"
              data-testid="leave-review-btn"
            >
              {t('reviews_leave') || 'Leave a Review'}
            </button>
          ) : (
            <div className="max-w-lg mx-auto bg-[#0d0d0d] border border-[#D4AF37]/30 p-8">
              {submitted ? (
                <div className="text-center py-4" data-testid="review-success">
                  <Star className="w-10 h-10 text-[#D4AF37] fill-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#D4AF37] text-lg font-serif" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {t('reviews_thanks') || 'Thank you for your review!'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none text-sm"
                      placeholder={t('reviews_name_placeholder') || 'Your name'}
                      required
                      data-testid="review-name-input"
                    />
                  </div>
                  <div className="flex justify-center">
                    {renderStarsInput()}
                  </div>
                  <div>
                    <textarea
                      value={form.text}
                      onChange={(e) => setForm({ ...form, text: e.target.value })}
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none text-sm resize-none"
                      rows={4}
                      placeholder={t('reviews_text_placeholder') || 'Share your experience...'}
                      required
                      data-testid="review-text-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 px-4 py-3 border border-white/10 text-[#A1A1AA] text-sm hover:border-white/30 transition-colors"
                    >
                      {t('close') || 'Cancel'}
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black font-medium text-sm uppercase tracking-wider hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50"
                      data-testid="review-submit-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {t('reviews_submit') || 'Submit'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
